import { CorrespondenceActionPartyDto } from '@cradua/data';
import { createReducer, on, Action } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';

import * as CorrespondenceActionPartyActions from './action-party.actions';

export const CORRESPONDENCE_ACTION_PARTY_FEATURE_KEY =
  'correspondenceActionParty';

export interface CorrespondenceActionPartyState
  extends EntityState<CorrespondenceActionPartyDto> {
  selectedCorrespondenceActionPartyId?: string | number; // which CorrespondenceActionParty record has been selected
  loaded: boolean; // has the CorrespondenceActionParty list been loaded
  error?: string | null; // last none error (if any)
}

export interface CorrespondenceActionPartyPartialState {
  readonly [CORRESPONDENCE_ACTION_PARTY_FEATURE_KEY]: CorrespondenceActionPartyState;
}

export const correspondenceActionPartyAdapter: EntityAdapter<CorrespondenceActionPartyDto> = createEntityAdapter<
  CorrespondenceActionPartyDto
>({
  selectId: (correspondenceActionParty) => correspondenceActionParty.id,
  sortComparer: false,
});

export const initialState: CorrespondenceActionPartyState = correspondenceActionPartyAdapter.getInitialState(
  {
    // set initial required properties
    loaded: false,
    error: null,
    selectedCorrespondenceActionPartyId: null,
  }
);

const correspondenceActionPartyReducer = createReducer(
  initialState,
  on(
    CorrespondenceActionPartyActions.loadCorrespondenceActionParty,
    (state) => ({
      ...state,
      loaded: true,
      error: null,
    })
  ),
  on(
    CorrespondenceActionPartyActions.loadCorrespondenceActionPartySuccess,
    (state, { correspondenceActionParty }) =>
      correspondenceActionPartyAdapter.addAll(correspondenceActionParty, {
        ...state,
        loaded: false,
      })
  ),
  on(
    CorrespondenceActionPartyActions.loadCorrespondenceActionPartyFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),
  on(
    CorrespondenceActionPartyActions.addCorrespondenceActionParty,
    (state) => ({
      ...state,
      loaded: true,
      error: null,
    })
  ),
  on(
    CorrespondenceActionPartyActions.addCorrespondenceActionPartySuccess,
    (state, { newCorrespondenceActionParty }) =>
      correspondenceActionPartyAdapter.addMany(newCorrespondenceActionParty, {
        ...state,
        loaded: false,
      })
  ),
  on(
    CorrespondenceActionPartyActions.addCorrespondenceActionPartyFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),
  on(
    CorrespondenceActionPartyActions.updateCorrespondenceActionParty,
    (state) => ({
      ...state,
      loaded: true,
      error: null,
    })
  ),
  on(
    CorrespondenceActionPartyActions.updateCorrespondenceActionPartySuccess,
    (state, { updateCorrespondenceActionParty }) =>
      correspondenceActionPartyAdapter.upsertMany(
        updateCorrespondenceActionParty,
        { ...state, loaded: false }
      )
  ),
  on(
    CorrespondenceActionPartyActions.updateCorrespondenceActionPartyFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),
  on(
    CorrespondenceActionPartyActions.removeCorrespondenceActionParty,
    (state) => ({
      ...state,
      loaded: true,
      error: null,
    })
  ),
  on(
    CorrespondenceActionPartyActions.removeCorrespondenceActionPartySuccess,
    (state, { ids }) =>
      correspondenceActionPartyAdapter.removeMany(ids, {
        ...state,
        loaded: false,
      })
  ),
  on(
    CorrespondenceActionPartyActions.removeCorrespondenceActionPartyFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  )
);

export function reducer(
  state: CorrespondenceActionPartyState | undefined,
  action: Action
) {
  return correspondenceActionPartyReducer(state, action);
}
